import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, FormError, FormSuccess } from "@/components/FormField";
import { t } from "@/lib/i18n";

export default function MfaSetupPage({ embedded = false }) {
  const { user, refreshUser } = useAuth();
  const location = useLocation();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setError(""); setBusy(true);
    try { const { data } = await api.post("/auth/mfa/enroll/start"); setSetup(data); } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };
  const confirm = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/enroll/confirm", { code });
      setBackupCodes(data.backup_codes); setSetup(null); setCode("");
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };
  const finishBackup = async () => { setBackupCodes(null); await refreshUser(); setNotice(t("mfa.isOn")); };
  const disable = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try { await api.post("/auth/mfa/disable", { code }); setCode(""); await refreshUser(); setNotice(t("mfa.disabled")); } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  const graceDate = user?.mfa_grace_ends_at ? new Date(user.mfa_grace_ends_at).toLocaleDateString("en-US", { month: "long", day: "numeric", timeZone: "America/Los_Angeles" }) : null;

  return (
    <div className={embedded ? "" : "mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16"} data-testid="mfa-page">
      <div className="max-w-md">
        <h1 className={embedded ? "text-3xl" : "text-4xl sm:text-5xl"}>{t("mfa.title")}</h1>
        <p className="mt-4 text-ink-500">{t("mfa.intro")}</p>
        {user?.is_staff && !user.mfa_enabled && (
          <p className={`mt-4 rounded border px-4 py-3 ${user.mfa_enrollment_required || location.state?.required ? "border-terracotta-600" : "border-sage-400"} bg-cream-100`} role="status" data-testid="mfa-staff-notice">
            {user.mfa_enrollment_required ? t("mfa.staffRequiredNow") : t("mfa.staffRequired", { date: graceDate })}
          </p>
        )}
        <div className="mt-8">
          <FormSuccess message={notice} testId="mfa-notice" />
          <FormError message={error} testId="mfa-error" />

          {backupCodes && (
            <div className="surface p-6 confirm-pop" data-testid="mfa-backup-codes">
              <p className="font-serif text-xl font-semibold">{t("mfa.enabled")}</p>
              <ul className="mt-4 grid grid-cols-2 gap-2 font-mono text-lg">{backupCodes.map((c) => <li key={c} className="rounded bg-cream-50 px-3 py-2">{c}</li>)}</ul>
              <button type="button" className="btn-primary mt-6 w-full" onClick={finishBackup} data-testid="mfa-backup-done">{t("mfa.done")}</button>
            </div>
          )}

          {!backupCodes && !user?.mfa_enabled && !setup && (
            <button type="button" className="btn-primary" onClick={start} disabled={busy} data-testid="mfa-start">{t("mfa.start")}</button>
          )}

          {setup && (
            <form onSubmit={confirm} noValidate className="surface p-6" data-testid="mfa-setup">
              <p>{t("mfa.scan")}</p>
              <img src={`data:image/svg+xml;utf8,${encodeURIComponent(setup.qr_svg)}`} alt="QR code for your authenticator app" className="mt-4 h-48 w-48 rounded bg-white p-3" data-testid="mfa-qr" />
              <p className="mt-3 text-sm text-ink-500">{t("mfa.key")}: <code className="select-all break-all font-mono text-ink-900" data-testid="mfa-secret">{setup.secret}</code></p>
              <div className="mt-6">
                <Field label={t("mfa.enterCode")} required>
                  {(p) => <input {...p} inputMode="numeric" autoComplete="one-time-code" className="input text-2xl tracking-[0.3em]" value={code} onChange={(e) => setCode(e.target.value)} data-testid="mfa-confirm-code" />}
                </Field>
                <button type="submit" className="btn-primary w-full" disabled={busy} data-testid="mfa-confirm">{t("mfa.confirm")}</button>
              </div>
            </form>
          )}

          {user?.mfa_enabled && !backupCodes && (
            <div className="surface p-6" data-testid="mfa-on">
              <p className="font-serif text-xl font-semibold">{t("mfa.isOn")}</p>
              {user.is_staff ? (
                <p className="mt-2 text-ink-500">{t("mfa.staffCannotDisable")}</p>
              ) : (
                <form onSubmit={disable} noValidate className="mt-5">
                  <Field label={t("mfa.disableHelp")}>
                    {(p) => <input {...p} inputMode="numeric" className="input" value={code} onChange={(e) => setCode(e.target.value)} data-testid="mfa-disable-code" />}
                  </Field>
                  <button type="submit" className="btn-secondary" disabled={busy} data-testid="mfa-disable">{t("mfa.disable")}</button>
                </form>
              )}
            </div>
          )}
        </div>
        {!embedded && <p className="mt-8"><Link to={user?.is_staff ? "/staff" : "/portal"} className="link" data-testid="mfa-continue-link">{user?.is_staff ? t("nav.staffConsole") : t("nav.myCare")}</Link></p>}
      </div>
    </div>
  );
}
